import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import { trackPurchase } from '@/utils/metaPixel';

const PaymentSuccess = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const applicationId = location.state?.applicationId;
  const amount = location.state?.amount;
  const visaType = location.state?.visaType;

  // Track purchase on page load
  useEffect(() => {
    trackPurchase({
      applicationId: applicationId,
      value: amount || 0,
      visaType: visaType || 'UAE Visa',
      numItems: 1
    });
  }, [applicationId, amount, visaType]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        <Card className="border-slate-200 shadow-xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>

            <h1 className="text-3xl font-heading font-bold text-[#1A4D7F] mb-3">
              {t.paymentSuccess.title}
            </h1>
            <p className="text-slate-600 mb-6 leading-relaxed">
              {t.paymentSuccess.subtitle}
            </p>

            {applicationId && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500 mb-1">{t.paymentSuccess.applicationId}</p>
                <p className="font-mono text-sm font-medium text-[#1A4D7F]">{applicationId}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-medium text-blue-900 mb-2">{t.paymentSuccess.whatNext}</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {t.paymentSuccess.step1}</li>
                <li>• {t.paymentSuccess.step2}</li>
                <li>• {t.paymentSuccess.step3}</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/track')}
                className="w-full bg-[#1A4D7F] hover:bg-[#153e66]"
                data-testid="track-application-button"
              >
                {t.paymentSuccess.trackApplication}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
                data-testid="back-to-home-button"
              >
                {t.paymentSuccess.backHome}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
